import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { Spinner, ToastProvider } from '@/components/ui'
import AppShell from '@/components/layout/AppShell'
import ErrorBoundary from '@/components/layout/ErrorBoundary'
import RequireAdmin from '@/components/layout/RequireAdmin'
import RequireAuth from '@/components/layout/RequireAuth'
import type { AuthRedirectState } from '@/components/layout/RequireAuth'
import LoginPage from '@/components/auth/LoginPage'
import SetupPage from '@/components/auth/SetupPage'
import SitesPage from '@/components/sites/SitesPage'
import KeysPage from '@/components/keys/KeysPage'
import MediaPage from '@/components/media/MediaPage'
import SettingsPage from '@/components/settings/SettingsPage'
import AdminPage from '@/components/admin/AdminPage'
import AboutPage from '@/components/about/AboutPage'
import { useAuthStore } from '@/stores/useAuthStore'

const ROUTE_TITLES: Array<[RegExp, string]> = [
  [/^\/login/, 'Sign in'],
  [/^\/setup/, 'Set up Argus'],
  [/^\/sites\/[^/]+/, 'Media'],
  [/^\/sites/, 'Sites'],
  [/^\/keys/, 'SSH keys'],
  [/^\/settings/, 'Settings'],
  [/^\/admin/, 'Admin'],
  [/^\/about/, 'About']
]

/**
 * Applies a per-route default `document.title` during render, so a page's own
 * `useDocumentTitle` effect — which commits afterwards — always wins.
 */
function RouteTitle(): null {
  const { pathname } = useLocation()
  const applied = useRef<string | null>(null)

  if (applied.current !== pathname) {
    applied.current = pathname
    const label = ROUTE_TITLES.find(([pattern]) => pattern.test(pathname))?.[1]
    document.title = label ? `${label} · Argus` : 'Argus'
  }

  return null
}

function BootSplash(): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full w-full flex-col items-center justify-center gap-5 bg-canvas p-6"
    >
      <span className="grid h-12 w-12 place-items-center rounded-card bg-accent text-accent-fg shadow-sheet">
        <Sparkles className="h-6 w-6" aria-hidden="true" />
      </span>
      <Spinner size="sm" />
      <span className="sr-only">Loading Argus</span>
    </div>
  )
}

/** Where to land after a successful sign-in, ignoring anything unsafe. */
function resolveReturnPath(state: unknown): string {
  const from = (state as AuthRedirectState | null)?.from
  if (typeof from !== 'string' || !from.startsWith('/')) return '/sites'
  // `//host` is a protocol-relative URL, and bouncing back to an auth screen loops.
  const unsafe = ['//', '/login', '/setup'].some((prefix) => from.startsWith(prefix))
  return unsafe ? '/sites' : from
}

function PublicOnlyRoute({ children }: { children: ReactNode }): React.ReactElement {
  const location = useLocation()
  const session = useAuthStore((state) => state.session)
  const user = useAuthStore((state) => state.user)

  if (session?.needsBootstrap) return <Navigate to="/setup" replace />
  if (user) return <Navigate to={resolveReturnPath(location.state)} replace />

  return <>{children}</>
}

function SetupRoute({ children }: { children: ReactNode }): React.ReactElement {
  const session = useAuthStore((state) => state.session)
  const user = useAuthStore((state) => state.user)

  if (session && !session.needsBootstrap) {
    return <Navigate to={user ? '/sites' : '/login'} replace />
  }

  return <>{children}</>
}

function AuthedLayout(): React.ReactElement {
  return (
    <RequireAuth>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuth>
  )
}

function AppRoutes(): React.ReactElement {
  const loadSession = useAuthStore((state) => state.loadSession)
  const [booted, setBooted] = useState(false)

  // Nothing may render before the session resolves, or an authenticated user
  // sees the login screen flash on every reload.
  useEffect(() => {
    let live = true

    const boot = async (): Promise<void> => {
      try {
        await loadSession()
      } finally {
        if (live) setBooted(true)
      }
    }

    void boot()

    return () => {
      live = false
    }
  }, [loadSession])

  if (!booted) return <BootSplash />

  return (
    <>
      <RouteTitle />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/setup"
          element={
            <SetupRoute>
              <SetupPage />
            </SetupRoute>
          }
        />

        <Route element={<AuthedLayout />}>
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/sites/:siteId" element={<MediaPage />} />
          <Route path="/keys" element={<KeysPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            }
          />
          <Route path="/about" element={<AboutPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/sites" replace />} />
      </Routes>
    </>
  )
}

export default function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  )
}
